import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import { ILink } from '@models/ILink';
import useOnClickOutside from '@utility/hooks/useOnClickOutside';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
type ButtonType = { label: string; onClick: () => void };
type Options = Array<ButtonType | ILink>;

export enum DropdownVariant {
  Primary = 'primary',
  Secondary = 'secondary',
}

export interface IDropdown {
  triggerLabel: string;
  options: Options;
  containerProps?: React.ComponentProps<'div'>;
  buttonClasses?: string;
  variant?: DropdownVariant;
  activeIndex?: number;
}

const DropdownContent = ({
  options,
  closeDropdown,
  activeIndex,
}: {
  options: Options;
  closeDropdown: () => void;
  activeIndex?: number;
}) => {
  const itemsRef = useRef<Array<HTMLButtonElement | HTMLAnchorElement>>([]);

  const getFocusedIndex = () => {
    if (typeof document === 'undefined') return -1;

    const focusedItem = itemsRef.current?.find((item) =>
      item.contains(document.activeElement)
    );
    return focusedItem ? itemsRef.current.indexOf(focusedItem) : -1;
  };

  return (
    <ul
      className="tw-outline-none"
      onKeyDown={(event) => {
        const focusedIndex = getFocusedIndex();

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            event.stopPropagation();

            if (focusedIndex < itemsRef.current.length - 1) {
              itemsRef.current[getFocusedIndex() + 1]?.focus();
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            event.stopPropagation();

            if (focusedIndex > 0) {
              itemsRef.current[getFocusedIndex() - 1]?.focus();
            }
            break;
          case 'Tab':
          case 'Shift':
            break;
          default:
            closeDropdown();
            break;
        }
      }}
    >
      {options.map((option, i) => {
        const isLink = isLinkType(option);
        const sharedStyles =
          'tw-w-full tw-px-4 tw-py-3.5 tw-text-left tw-text-sm  tw-text-grey-100 tw-outline-none focus-within:tw-bg-grey-20 hover:tw-bg-grey-10 rtl:tw-text-right';

        return isLink ? (
          <li key={option.text}>
            <a
              className={classNames(
                sharedStyles,
                'tw-block tw-whitespace-nowrap',
                i === activeIndex && 'tw-font-semibold'
              )}
              href={option.url}
              target={option.target}
              ref={(el) => {
                if (itemsRef?.current && el) {
                  itemsRef.current[i] = el;
                }
              }}
            >
              {option.text}
            </a>
          </li>
        ) : (
          <li key={option.label}>
            <button
              className={sharedStyles}
              onClick={() => {
                option.onClick();
                closeDropdown();
              }}
              ref={(el) => {
                if (itemsRef?.current && el) {
                  itemsRef.current[i] = el;
                }
              }}
            >
              {option.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const Dropdown = ({
  triggerLabel,
  options,
  containerProps,
  buttonClasses,
  variant = DropdownVariant.Primary,
  activeIndex,
}: IDropdown) => {
  const [isOpen, setIsOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useOnClickOutside(container, () => setIsOpen(false));

  return (
    <div
      {...containerProps}
      ref={container}
      className={classNames('tw-relative', containerProps?.className)}
    >
      {variant === DropdownVariant.Primary ? (
        <Button
          label={triggerLabel}
          variant="grey80"
          modifier="outline"
          onClick={() => setIsOpen(!isOpen)}
          buttonClasses={buttonClasses}
        />
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={classNames('tw-flex tw-gap-2.5', buttonClasses)}
        >
          <span className="tw-max-w-[calc(100%-26px)] tw-overflow-hidden tw-overflow-ellipsis tw-whitespace-nowrap">
            {triggerLabel}
          </span>
          <Icon
            name="chevron-down"
            size={16}
            className={classNames(
              'tw-my-auto tw-shrink-0 tw-transition-transform',
              isOpen && 'tw-rotate-180'
            )}
          />
        </button>
      )}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            className={classNames(
              'tw-absolute  tw-top-[calc(100%+1rem)] tw-z-10 tw-min-w-[224px] tw-overflow-hidden tw-rounded tw-border tw-border-grey-30 tw-bg-white tw-shadow-md',
              {
                '-tw-left-[15px] -tw-right-[15px] md:tw-left-auto md:tw-right-0':
                  variant === DropdownVariant.Secondary,
              }
            )}
            transition={{ delay: 0.1, duration: 0.3, ease: 'easeInOut' }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <DropdownContent
              options={options}
              closeDropdown={() => setIsOpen(false)}
              activeIndex={activeIndex}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const isLinkType = (option: ButtonType | ILink): option is ILink => {
  return (option as ILink).text !== undefined;
};

export default Dropdown;
